import { createFocusTrap } from 'focus-trap';

class FocusTrapStack {
    constructor() {
        this.traps = new Map();
        this.activeTraps = [];
    }

    /**
     * Register a focus trap with an ID
     * @param {string} trapId - Unique identifier for the trap
     * @param {HTMLElement} element - The element to trap focus within
     */
    register(trapId, element) {
        if (!element || !trapId) {
            throw new Error('Both trapId and element are required');
        }

        if (this.traps.has(trapId)) {
            return;
        }

        const trap = createFocusTrap(element, {
            clickOutsideDeactivates: true,
            escapeDeactivates: true,
            onDeactivate: () => {
                this._handleTrapDeactivation(trapId);
            }
        });

        this.traps.set(trapId, {
            trap,
            element,
            isActive: false
        });
    }

    /**
     * Activate a specific trap by ID
     * @param {string} trapId - The ID of the trap to activate
     */
    activate(trapId) {
        const trapEntry = this.traps.get(trapId);

        if (!trapEntry) {
            throw new Error(`No trap found with ID "${trapId}"`);
        }

        if (trapEntry.isActive) {
            console.warn(`Trap "${trapId}" is already active`);
            return;
        }

        const currentActive = this.activeTraps[this.activeTraps.length - 1];
        if (currentActive && currentActive.trapId !== trapId) {
            const currentTrapEntry = this.traps.get(currentActive.trapId);
            if (currentTrapEntry) {
                currentTrapEntry.trap.deactivate();
                currentTrapEntry.isActive = false;
            }
        }

        trapEntry.trap.activate();
        trapEntry.isActive = true;

        this.activeTraps.push({ trapId, timestamp: Date.now() });
    }

    /**
     * Deactivate a specific trap by ID
     * @param {string} trapId - The ID of the trap to deactivate
     */
    deactivate(trapId) {
        const trapEntry = this.traps.get(trapId);

        if (!trapEntry) {
            console.warn(`No trap found with ID "${trapId}"`);
            return;
        }

        if (!trapEntry.isActive) {
            return;
        }

        trapEntry.trap.deactivate();
        trapEntry.isActive = false;

        this.activeTraps = this.activeTraps.filter(active => active.trapId !== trapId);

        const mostRecent = this.activeTraps[this.activeTraps.length - 1];
        if (mostRecent) {
            const mostRecentTrap = this.traps.get(mostRecent.trapId);
            if (mostRecentTrap) {
                mostRecentTrap.trap.activate();
                mostRecentTrap.isActive = true;
            }
        }
    }

    /**
     * Deregister a trap completely
     * @param {string} trapId - The ID of the trap to deregister
     */
    deregister(trapId) {
        const trapEntry = this.traps.get(trapId);

        if (!trapEntry) {
            console.warn(`No trap found with ID "${trapId}"`);
            return;
        }

        if (trapEntry.isActive) {
            this.deactivate(trapId);
        }

        this.traps.delete(trapId);
    }

    /**
     * Check if a trap is active
     * @param {string} trapId - The ID to check
     * @returns {boolean}
     */
    isActive(trapId) {
        const trapEntry = this.traps.get(trapId);
        return trapEntry ? trapEntry.isActive : false;
    }

    /**
     * check if trap exists
     * @param {string} trapId
     * @return {boolean}
     */
    hasTrap(trapId) {
        return Boolean(this.traps.has(trapId));
    }

    /**
     * Internal method to handle trap deactivation from focus-trap library
     * @private
     */
    _handleTrapDeactivation(trapId) {
        const trapEntry = this.traps.get(trapId);
        if (trapEntry) {
            trapEntry.isActive = false;
        }

        this.activeTraps = this.activeTraps.filter(active => active.trapId !== trapId);

        const mostRecent = this.activeTraps[this.activeTraps.length - 1];
        if (mostRecent) {
            const mostRecentTrap = this.traps.get(mostRecent.trapId);
            if (mostRecentTrap && !mostRecentTrap.isActive) {
                mostRecentTrap.trap.activate();
                mostRecentTrap.isActive = true;
            }
        }
    }
}

export default FocusTrapStack;